import type { AuthAccount, AuthUser, UserProfile } from "../types/auth";

export interface StoredAuth {
  user: AuthUser;
  account: AuthAccount;
}

export function profileToStoredAuth(
  profile: UserProfile,
  fallbackAccountName: string,
  current: StoredAuth | null = loadAuth(),
): StoredAuth {
  if (!current?.user.session_jwt || current.user.id !== profile.id
      || current.user.account_id !== profile.account_id) {
    throw new Error("Sign in as this user to switch identities.");
  }
  const user: AuthUser = {
    id: profile.id,
    name: profile.name,
    email: profile.email,
    verified: profile.verified,
    stytch_user_id: profile.stytch_user_id,
    account_id: profile.account_id,
    session_jwt: current.user.session_jwt,
    role: profile.role,
    status: profile.status,
    role_id: profile.role_id,
    stytch_org_id: profile.stytch_org_id,
    user_info: profile.user_info ?? null,
  };
  return {
    user,
    account: {
      id: profile.account_id,
      name: fallbackAccountName,
      stytch_org_id: profile.stytch_org_id ?? "",
    },
  };
}

const KEY = "ess.auth.v2";
const ORIGINAL_KEY = "ess.auth.original.v2";
const LEGACY_KEY = "ess.auth.v1";

// "Keep me signed in" semantics:
//   - persistent=true  → localStorage (survives browser restart)
//   - persistent=false → sessionStorage (cleared when the tab closes)
// loadAuth checks sessionStorage first so that an active no-keep session
// takes precedence over a stale localStorage entry from a prior login.
export function loadAuth(): StoredAuth | null {
  try {
    const raw =
      sessionStorage.getItem(KEY) ?? localStorage.getItem(KEY);
    if (!raw) return null;
    const auth = JSON.parse(raw) as StoredAuth;
    if (!auth.user?.session_jwt?.trim() || !auth.user.id
        || auth.user.account_id !== auth.account?.id) {
      clearAuth();
      return null;
    }
    return auth;
  } catch {
    return null;
  }
}

export function saveAuth(auth: StoredAuth, persistent = true): void {
  if (!auth.user.session_jwt?.trim() || auth.user.account_id !== auth.account.id) {
    throw new Error("Sign-in did not return an authenticated session.");
  }
  const serialized = JSON.stringify(auth);
  // Always clear the opposite store so we don't leave a stale copy.
  if (persistent) {
    localStorage.setItem(KEY, serialized);
    sessionStorage.removeItem(KEY);
  } else {
    sessionStorage.setItem(KEY, serialized);
    localStorage.removeItem(KEY);
  }
  localStorage.removeItem(LEGACY_KEY);
}

export function clearAuth(): void {
  localStorage.removeItem(KEY);
  sessionStorage.removeItem(KEY);
  localStorage.removeItem(ORIGINAL_KEY);
  sessionStorage.removeItem(ORIGINAL_KEY);
  localStorage.removeItem(LEGACY_KEY);
}

export function loadOriginalAuth(): StoredAuth | null {
  try {
    const raw =
      sessionStorage.getItem(ORIGINAL_KEY) ??
      localStorage.getItem(ORIGINAL_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredAuth;
  } catch {
    return null;
  }
}

// Original (pre-impersonation) auth shadows the active auth's storage
// tier — if the user kept signed in, we keep the original around too;
// if they're in a session-only login, the original lives in
// sessionStorage so it's cleared on tab close just like the active.
export function saveOriginalAuth(auth: StoredAuth): void {
  const persistent = localStorage.getItem(KEY) !== null;
  const serialized = JSON.stringify(auth);
  if (persistent) {
    localStorage.setItem(ORIGINAL_KEY, serialized);
    sessionStorage.removeItem(ORIGINAL_KEY);
  } else {
    sessionStorage.setItem(ORIGINAL_KEY, serialized);
    localStorage.removeItem(ORIGINAL_KEY);
  }
}

export function clearOriginalAuth(): void {
  localStorage.removeItem(ORIGINAL_KEY);
  sessionStorage.removeItem(ORIGINAL_KEY);
}

export function getAuthHeaders(): Record<string, string> {
  const auth = loadAuth();
  if (!auth) return {};
  return {
    "x-session-jwt": auth.user.session_jwt!,
    "x-account-id": auth.user.account_id ?? auth.account.id,
    "x-user-id": auth.user.id,
  };
}

export function isHrRole(role: string | undefined | null): boolean {
  if (!role) return false;
  const r = role.toLowerCase();
  return r !== "employee";
}

export function isAdminRole(role: string | undefined | null): boolean {
  if (!role) return false;
  return role.toLowerCase() === "admin";
}

export const SESSION_EXPIRED_EVENT = "ess-session-expired";

export function expireSession(token: string | undefined): void {
  if (!token || loadAuth()?.user.session_jwt !== token) return;
  clearAuth();
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
}

export function isAuthPersistent(): boolean {
  return localStorage.getItem(KEY) !== null;
}
